import { useState, useEffect, useMemo, useRef } from 'react'
import { ChevronRight } from 'lucide-react'
import { cn } from '@utils'
import { VariantType, StepCategory } from '@/constants'
import { useSessionStore } from '@/stores'
import { useUIStore } from '@/stores'
import { apiClient } from '@/services/api'
import { getDefaultProductImageUrl } from '@/utils/image'
import { updateFontSizes } from '@/utils/fontSizing'
import { parseStyleProp } from '@/utils/dom'
import StepTitle from '@/components/StepTitle'
import ProductImageZoom from '@/components/ProductImageZoom'
import VariantSelector from './VariantSelector'
import VariantQuantities from './VariantQuantities'

interface VariantsProps {
  item?: any
  newCampaignProduct?: any
  quantity?: number
  subscriptionOnly?: boolean
  session?: any
  onSelect?: (data: any) => void
  onBack?: () => void
}

const Variants = ({
  item,
  newCampaignProduct,
  quantity = 1,
  subscriptionOnly = false,
  session: sessionProp,
  onSelect,
  onBack,
}: VariantsProps) => {
  // In the OMP action drawer the live step is popupSession; the store session is
  // the portal-step background and would drive the wrong labels/selected_item.
  const storeSession = useSessionStore(state => state.session)
  const session = sessionProp ?? storeSession
  const setLoading = useUIStore(state => state.setLoading)
  const addUIMessage = useUIStore(state => state.addUIMessage)
  const setCriticalError = useUIStore(state => state.setCriticalError)

  const [variants, setVariants] = useState<Record<string, string[]>>({})
  const [selections, setSelections] = useState<Record<string, (string | null)[]>>({})
  const [idMap, setIdMap] = useState<Record<string, number>>({})
  const [selectedQuantities, setSelectedQuantities] = useState<Record<string, any>>({})
  const [product, setProduct] = useState<any>(null)
  const [bundleQuantity, setBundleQuantity] = useState(1)
  const [rawVariants, setRawVariants] = useState<any[]>([])
  const [initialVariant, setInitialVariant] = useState<any>(null)
  const [, setInitialSelectionComplete] = useState(false)

  // Initial pre-fill, re-applied on reprocess so a bundleQuantity change doesn't wipe it.
  const initialVariantRef = useRef<any>(null)
  const initialBundledVariantsRef = useRef<any[]>([])

  const isBundledVariant = useMemo(() => product?.variant_type === VariantType.Bundled, [product])

  const expectedQuantity = useMemo(
    () => (isBundledVariant ? quantity * bundleQuantity : quantity),
    [isBundledVariant, quantity, bundleQuantity]
  )

  const dropdownSelectorCount = useMemo(
    () => (bundleQuantity > 1 ? expectedQuantity : 1),
    [bundleQuantity, expectedQuantity]
  )

  const useQuantitySelections = useMemo(() => {
    if (bundleQuantity > 1 && expectedQuantity > 1) {
      const totalChoices = Object.keys(variants).length * expectedQuantity
      return totalChoices > 10
    }
    return false
  }, [bundleQuantity, expectedQuantity, variants])

  const showStepLabels = useMemo(() => (session as any)?.step_category === StepCategory.ChangeVariant, [session])

  const subtitle = useMemo(() => {
    if (useQuantitySelections) {
      return undefined
    }
    if (expectedQuantity > 1) {
      return `Select options for ${expectedQuantity} items`
    }
    return undefined
  }, [useQuantitySelections, expectedQuantity])

  const getImageUrl = useMemo(() => (product ? getDefaultProductImageUrl(product, 'large') : ''), [product])

  const variantById = useMemo(() => {
    const map: Record<number, any> = {}
    rawVariants.forEach(v => {
      map[v.id] = v
    })
    return map
  }, [rawVariants])

  const currentSelectionSummary = useMemo(() => {
    if (dropdownSelectorCount !== 1) {
      return [] as { name: string; value: string }[]
    }
    return Object.keys(selections)
      .sort()
      .map(name => ({ name, value: selections[name]?.[0] as string }))
      .filter(o => o.value)
  }, [selections, dropdownSelectorCount])

  // Sorted `name=value` key matching the idMap scheme; empty until fully chosen.
  const currentSelectionKey = useMemo(() => {
    if (dropdownSelectorCount !== 1 || !currentSelectionSummary.length) {
      return ''
    }
    const names = Object.keys(selections)
    if (currentSelectionSummary.length !== names.length) {
      return ''
    }
    return currentSelectionSummary
      .map(o => `${o.name}=${o.value}`)
      .sort()
      .join(',')
  }, [currentSelectionSummary, selections, dropdownSelectorCount])

  const selectedVariantImageUrl = useMemo(() => {
    if (!currentSelectionKey) {
      return ''
    }
    const id = idMap[currentSelectionKey]
    const file = id !== undefined ? variantById[id]?.image?.file : null
    // original first: staging doesn't always generate thumbnails, so `large` 404s.
    return file ? file.original || file.large || file.medium || '' : ''
  }, [currentSelectionKey, idMap, variantById])

  const previousOptions = useMemo(
    () => ((initialVariant?.options || []) as any[]).filter(o => !o.is_deleted),
    [initialVariant]
  )
  const showPreviousNote = !newCampaignProduct && previousOptions.length > 0

  const previousSelectionKey = useMemo(
    () =>
      previousOptions.length
        ? previousOptions
            .map((o: any) => `${o.name}=${o.value}`)
            .sort()
            .join(',')
        : '',
    [previousOptions]
  )

  // Swap steps must land on a real change, so block re-submitting the existing variant.
  const isSameAsPrevious = showStepLabels && !!currentSelectionKey && currentSelectionKey === previousSelectionKey

  const processVariants = (
    variantsData: any[],
    currentProduct: any,
    effectiveBundleQty: number,
    selectedVariant: any,
    bundledVariants: any[],
    prefillSelections: boolean = true
  ) => {
    const isBundled = currentProduct.variant_type === VariantType.Bundled
    const newExpectedQty = isBundled ? quantity * effectiveBundleQty : quantity
    const selectorCount = effectiveBundleQty > 1 ? newExpectedQty : 1

    const variantOptionMap: Record<string, Record<string, boolean>> = {}
    const variantOptions: Record<string, string[]> = {}
    const newSelections: Record<string, (string | null)[]> = {}
    const newIdMap: Record<string, number> = {}
    const newSelectedQuantities: Record<string, any> = {}

    variantsData.forEach((variant: any) => {
      const pairs: string[] = []
      const options: Record<string, string> = {}

      variant.options.forEach((option: any) => {
        if (!(option.name in variantOptionMap)) {
          variantOptionMap[option.name] = {}
        }
        variantOptionMap[option.name][option.value] = true
        pairs.push(option.name + '=' + option.value)
        options[option.name] = option.value
      })

      const key = pairs.sort().join(',')
      newIdMap[key] = variant.id
      newSelectedQuantities[variant.id] = { quantity: 0, options, key }
    })

    Object.keys(variantOptionMap).forEach(optionName => {
      variantOptions[optionName] = Object.keys(variantOptionMap[optionName]).sort()
      newSelections[optionName] = Array(selectorCount).fill(null)
    })

    // Swap steps start empty so the customer actively picks a new option.
    if (prefillSelections) {
      if (bundledVariants?.length) {
        let startIndex = 0
        bundledVariants.forEach((bundledVariant: any) => {
          if (newSelectedQuantities[bundledVariant.variant.id]) {
            newSelectedQuantities[bundledVariant.variant.id].quantity = bundledVariant.quantity
          }
          if (effectiveBundleQty > 1) {
            for (let i = 0; i < bundledVariant.quantity && startIndex + i < selectorCount; i++) {
              for (const option of bundledVariant.variant.options) {
                if (newSelections[option.name]) {
                  newSelections[option.name][startIndex + i] = option.value
                }
              }
            }
            startIndex += bundledVariant.quantity
          }
        })
      } else if (selectedVariant?.options) {
        if (newSelectedQuantities[selectedVariant.id]) {
          newSelectedQuantities[selectedVariant.id].quantity = newExpectedQty
        }
        for (let i = 0; i < selectorCount; i++) {
          for (const option of selectedVariant.options) {
            if (newSelections[option.name]) {
              newSelections[option.name][i] = option.value
            }
          }
        }
      }
    }

    setVariants(variantOptions)
    setIdMap(newIdMap)
    setSelectedQuantities(newSelectedQuantities)
    setSelections(newSelections)
    setInitialSelectionComplete(true)
  }

  useEffect(() => {
    if (!item && !newCampaignProduct) {
      return
    }

    let currentProduct = item?.product
    let currentBundleQuantity = item?.campaign_product?.bundle_quantity || 1

    if (newCampaignProduct) {
      currentProduct = newCampaignProduct.product
      currentBundleQuantity = newCampaignProduct.bundle_quantity || 1
    } else if (subscriptionOnly && item) {
      currentProduct = item.next_cycle_product
      currentBundleQuantity = item.next_bundle_quantity || 1
    }

    if (!currentProduct) {
      return
    }

    setLoading(true)
    const loadVariants = async () => {
      try {
        // Saved selection lives only on the item-detail endpoint, not the list payload.
        const detailPromise =
          item?.id && !newCampaignProduct
            ? apiClient
                .get(`/order_items/${item.id}/`)
                .then(res => res.data || item)
                .catch(() => item)
            : Promise.resolve(item)

        const [response, detail] = await Promise.all([
          apiClient.get(`/products/${currentProduct.id}/variants/`),
          detailPromise,
        ])

        const variantsData = response.data

        let selectedVariant = detail?.variant
        let bundledVariants = detail?.bundled_variants || []
        if (newCampaignProduct) {
          selectedVariant = null
          bundledVariants = []
        } else if (subscriptionOnly) {
          selectedVariant = detail?.next_variant
          bundledVariants = detail?.next_bundled_variants || []
        }

        const effectiveBundleQty = currentBundleQuantity || 1
        initialVariantRef.current = selectedVariant
        initialBundledVariantsRef.current = bundledVariants
        setInitialVariant(selectedVariant)
        setProduct(currentProduct)
        setBundleQuantity(effectiveBundleQty)
        setRawVariants(variantsData)
        processVariants(
          variantsData,
          currentProduct,
          effectiveBundleQty,
          selectedVariant,
          bundledVariants,
          !showStepLabels
        )
      } catch (error) {
        console.error(error)
        setCriticalError(error)
      } finally {
        setLoading(false)
      }
    }
    loadVariants()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- processVariants is re-created each render; this must key on the product only.
  }, [item, newCampaignProduct, subscriptionOnly])

  useEffect(() => {
    if (!rawVariants.length || !product) {
      return
    }
    // Non-bundled (bundleQuantity 1) skips reprocess so in-progress picks survive.
    if (bundleQuantity === 1) {
      return
    }
    processVariants(
      rawVariants,
      product,
      bundleQuantity,
      initialVariantRef.current,
      initialBundledVariantsRef.current,
      !showStepLabels
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Recomputes on quantity change only; the source data is read, not tracked.
  }, [quantity, bundleQuantity])

  const updateSelection = (name: string, index: number, value: string | null) => {
    setSelections(prev => ({
      ...prev,
      [name]: prev[name].map((v, i) => (i === index ? value : v)),
    }))
  }

  const buildVariantSelections = (): { id: number; quantity: number }[] | null => {
    const quantities: Record<number, number> = {}
    const names = Object.keys(selections).sort()

    for (let i = 0; i < dropdownSelectorCount; i++) {
      const pairs: string[] = []

      for (const optionName of names) {
        const value = selections[optionName]?.[i]
        if (value === null || value === undefined || value === '') {
          addUIMessage({
            type: 'error',
            message: 'Please fill out all fields',
            persistent: false,
          })
          return null
        }
        pairs.push(optionName + '=' + value)
      }

      const id = idMap[pairs.sort().join(',')]
      if (id === undefined) {
        continue
      }
      quantities[id] = (quantities[id] || 0) + 1
    }

    return Object.entries(quantities).map(([id, qty]) => {
      const numericId = parseInt(id, 10)
      const finalQty = bundleQuantity > 1 ? qty : qty * quantity
      return { id: numericId, quantity: finalQty }
    })
  }

  const done = () => {
    if (isSameAsPrevious) {
      return
    }
    const variantSelections = buildVariantSelections()
    if (!variantSelections) {
      return
    }
    onSelect?.({ variants: variantSelections })
  }

  // Every option must have a non-empty value in every slot before we let the
  // user submit; otherwise buildVariantSelections returns null + toasts.
  const allSlotsFilled = useMemo(() => {
    const names = Object.keys(selections)
    if (!names.length) {
      return false
    }
    for (let i = 0; i < dropdownSelectorCount; i++) {
      for (const optionName of names) {
        const value = selections[optionName]?.[i]
        if (value === null || value === undefined || value === '') {
          return false
        }
      }
    }
    return true
  }, [selections, dropdownSelectorCount])

  const selectQuantities = (quantityMap: Record<string, any>) => {
    setSelectedQuantities(quantityMap)
    const variantSelections = Object.entries(quantityMap)
      .filter(([, row]) => {
        const q = parseInt(String(row.quantity))
        return !isNaN(q) && q > 0
      })
      .map(([id, row]) => ({ id: parseInt(id, 10), quantity: parseInt(String(row.quantity)) }))

    onSelect?.({ variants: variantSelections })
  }

  if (!product) {
    return null
  }

  const titleNode = showStepLabels ? (
    <div dangerouslySetInnerHTML={{ __html: session?.label || '' }} />
  ) : (
    <div>Choose Your Options</div>
  )

  const subtitleNode = showStepLabels ? (
    session?.subtitle ? (
      <div dangerouslySetInnerHTML={{ __html: session.subtitle }} />
    ) : undefined
  ) : (
    subtitle
  )

  const showBackButton = !!session?.can_undo || !!(session as any)?.is_step_preview

  return (
    <div>
      <StepTitle
        hr
        subtitle={subtitleNode}
        product={
          <div className="variant-product-card">
            <div className="variant-product-card-img">
              <ProductImageZoom src={getImageUrl} alt="" preview />
            </div>
            <div className="variant-product-card-info">
              <div className="name">
                {quantity} &times; <span dangerouslySetInnerHTML={{ __html: product.name }} />
              </div>
              {showPreviousNote && (
                <div className="variant-previous-note">
                  Previously ordered:{' '}
                  {previousOptions.map((opt, i) => (
                    <span key={opt.id ?? `${opt.name}-${i}`}>
                      {i > 0 && ', '}
                      {opt.name}: {opt.value}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        }>
        {titleNode}
      </StepTitle>

      {useQuantitySelections ? (
        <VariantQuantities
          selectedQuantities={selectedQuantities}
          totalQuantity={expectedQuantity}
          onChange={setSelectedQuantities}
          onSelect={selectQuantities}
          onBack={onBack}
        />
      ) : (
        <>
          <div className="w-full max-w-xl mx-auto px-2 mb-4 lg:px-4">
            <VariantSelector
              variants={variants}
              selections={selections}
              expectedQuantity={expectedQuantity}
              bundleQuantity={bundleQuantity}
              useQuantityMode={false}
              selectedQuantities={selectedQuantities}
              onSelectionChange={updateSelection}
            />
          </div>

          {selectedVariantImageUrl && (
            <div className="w-full max-w-xl mx-auto px-2 mb-4 lg:px-4">
              <div className="variant-new-selection">
                <div className="variant-new-selection-img">
                  <img
                    src={selectedVariantImageUrl}
                    alt=""
                    onError={e => {
                      const target = e.currentTarget
                      if (getImageUrl && target.src !== getImageUrl) {
                        target.src = getImageUrl
                      }
                    }}
                  />
                </div>
                <div className="variant-new-selection-detail">
                  <div className="variant-new-selection-label">Your new selection</div>
                  <div className="variant-new-selection-options">
                    {currentSelectionSummary.map(opt => (
                      <span key={opt.name}>
                        <span className="opt-name">{opt.name}:</span> {opt.value}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {isSameAsPrevious && (
            <div className="w-full max-w-xl mx-auto px-2 mb-4 lg:px-4">
              <div className="variant-same-hint">
                This is the same as your current order. Choose a different option to continue.
              </div>
            </div>
          )}

          {showStepLabels ? (
            <div className="flex flex-col items-center gap-3 mt-4 w-full">
              <div className="w-full md:w-7/12 flex flex-col items-center gap-2">
                {(session?.valid_inputs || []).map((input: any) => (
                  <button
                    key={`choice-${input.id}`}
                    type="button"
                    className="btn-base input-choice-btn input-btn w-full"
                    style={parseStyleProp(input.button_styles?.style)}
                    onClick={done}
                    disabled={!allSlotsFilled || isSameAsPrevious}>
                    <div dangerouslySetInnerHTML={{ __html: updateFontSizes(input.formatted_label || '') }} />
                  </button>
                ))}
                {showBackButton && (
                  <button type="button" className="btn-base back-btn w-full" onClick={() => onBack?.()}>
                    Back
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap justify-center items-center gap-3 mt-4 w-full">
              {showBackButton && (
                <div className="w-1/2 md:w-1/3">
                  <button type="button" className="btn-base back-btn w-full" onClick={() => onBack?.()}>
                    Back
                  </button>
                </div>
              )}
              <div className={cn(showBackButton ? 'w-1/2 md:w-1/3' : 'w-full md:w-2/3')}>
                <button
                  type="button"
                  className="btn-base input-choice-btn w-full inline-flex items-center justify-center gap-2"
                  onClick={done}
                  disabled={!allSlotsFilled || isSameAsPrevious}>
                  <span>Continue</span>
                  <ChevronRight size={18} aria-hidden="true" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default Variants
